/**
 * Simple sequential workflow model ↔ React Flow nodes/edges (backend BPM format).
 */

let _seq = 0;
const uid = (prefix) => `${prefix}-${Date.now()}-${++_seq}`;

export function newTask(overrides = {}) {
  return {
    id: uid('task'),
    type: 'task',
    label: '',
    description: '',
    status_value: '',
    assignee_role: '',
    ...overrides,
  };
}

export function newSplit() {
  return {
    id: uid('split'),
    type: 'split',
    label: 'Split condition',
    branches: [
      {
        id: uid('br'),
        label: 'Pass',
        condition: "qc_result == 'pass'",
        is_default: false,
        steps: [newTask({ label: 'Pass path', status_value: 'passed' })],
      },
      {
        id: uid('br'),
        label: 'Fail',
        condition: "qc_result == 'fail'",
        is_default: true,
        steps: [newTask({ label: 'Fail path', status_value: 'failed' })],
      },
    ],
  };
}

export function newParallel() {
  return {
    id: uid('parallel'),
    type: 'parallel',
    label: 'Parallel paths',
    lanes: [
      { id: uid('lane'), label: 'Path A', steps: [newTask({ label: 'Path A step' })] },
      { id: uid('lane'), label: 'Path B', steps: [newTask({ label: 'Path B step' })] },
    ],
  };
}

export function newSubprocess() {
  return {
    id: uid('sub'),
    type: 'subprocess',
    label: 'Sub-process',
    template_id: '',
    steps: [newTask({ label: 'Sub-process step' })],
  };
}

export function emptySimpleWorkflow() {
  return {
    name: '',
    description: '',
    blocks: [newTask({ label: 'Inspect', status_value: 'inspected' })],
  };
}

function taskNode(id, data, x) {
  return {
    id,
    type: 'task',
    position: { x, y: 200 },
    data: {
      label: data.label || 'Task',
      description: data.description || '',
      status_value: data.status_value || '',
      assignee_role: data.assignee_role || '',
      assignee_user_id: '',
    },
  };
}

function gatewayNode(id, type, label, x) {
  return { id, type, position: { x, y: 200 }, data: { label: label || type } };
}

class WorkflowCompiler {
  constructor() {
    this.nodes = [
      { id: 'start-1', type: 'start', position: { x: 80, y: 200 }, data: { label: 'Start' } },
    ];
    this.edges = [];
    this.x = 200;
  }

  nextX() {
    this.x += 160;
    return this.x;
  }

  edge(source, target, data = {}) {
    this.edges.push({
      id: uid('e'),
      source,
      target,
      data,
      label: data.label || '',
    });
  }

  connectPrev(prevIds, targetId) {
    (prevIds || []).forEach((p) => this.edge(p, targetId));
  }

  compileSteps(steps, prevIds) {
    let current = prevIds?.length ? prevIds : ['start-1'];
    for (const block of steps || []) {
      current = this.compileBlock(block, current);
    }
    return current;
  }

  compileBlock(block, prevIds) {
    if (!block) return prevIds;

    if (block.type === 'task') {
      const id = block.id?.startsWith('task-') ? block.id : uid('task');
      this.nodes.push(taskNode(id, block, this.nextX()));
      this.connectPrev(prevIds, id);
      return [id];
    }

    if (block.type === 'subprocess') {
      const headerId = uid('task');
      this.nodes.push(
        taskNode(headerId, {
          label: block.label || 'Sub-process',
          description: block.template_id ? `Ref: ${block.template_id}` : 'Inline sub-process',
          status_value: '',
          assignee_role: '',
        }, this.nextX()),
      );
      this.connectPrev(prevIds, headerId);
      const innerPrev = this.compileSteps(block.steps?.length ? block.steps : [newTask()], [headerId]);
      return innerPrev;
    }

    if (block.type === 'split') {
      const exId = uid('exclusive');
      this.nodes.push(gatewayNode(exId, 'exclusive', block.label || 'Split', this.nextX()));
      this.connectPrev(prevIds, exId);

      const branchExits = [];
      for (const branch of block.branches || []) {
        const branchEntry = uid('task');
        const firstStep = branch.steps?.[0] || newTask({ label: branch.label || 'Branch' });
        this.nodes.push(taskNode(branchEntry, firstStep, this.nextX()));
        this.edge(exId, branchEntry, {
          label: branch.label || '',
          condition: branch.condition || '',
          is_default: Boolean(branch.is_default),
        });
        const rest = branch.steps?.slice(1) || [];
        const exits = rest.length
          ? this.compileSteps(rest, [branchEntry])
          : [branchEntry];
        branchExits.push(...exits);
      }

      const mergeId = uid('merge');
      this.nodes.push(gatewayNode(mergeId, 'merge', 'Merge', this.nextX()));
      branchExits.forEach((b) => this.edge(b, mergeId));
      return [mergeId];
    }

    if (block.type === 'parallel') {
      const parId = uid('parallel');
      this.nodes.push(gatewayNode(parId, 'parallel', block.label || 'Parallel', this.nextX()));
      this.connectPrev(prevIds, parId);

      const laneExits = [];
      for (const lane of block.lanes || []) {
        const laneEntry = uid('task');
        const firstStep = lane.steps?.[0] || newTask({ label: lane.label || 'Lane' });
        this.nodes.push(taskNode(laneEntry, firstStep, this.nextX()));
        this.edge(parId, laneEntry, { label: lane.label || '' });
        const rest = lane.steps?.slice(1) || [];
        const exits = rest.length ? this.compileSteps(rest, [laneEntry]) : [laneEntry];
        laneExits.push(...exits);
      }

      const syncId = uid('sync');
      this.nodes.push(gatewayNode(syncId, 'sync', 'Sync', this.nextX()));
      laneExits.forEach((l) => this.edge(l, syncId));
      return [syncId];
    }

    return prevIds;
  }

  build(blocks) {
    const prev = this.compileSteps(blocks, ['start-1']);
    const endId = 'end-1';
    this.nodes.push({ id: endId, type: 'end', position: { x: this.nextX(), y: 200 }, data: { label: 'End' } });
    this.connectPrev(prev, endId);
    return { nodes: this.nodes, edges: this.edges };
  }
}

export function compileSimpleWorkflow(blocks) {
  return new WorkflowCompiler().build(blocks || []);
}

function outgoing(edges, nodeId) {
  return edges.filter((e) => e.source === nodeId);
}

function taskFromNode(node) {
  const d = node.data || {};
  return {
    id: node.id,
    type: 'task',
    label: d.label || '',
    description: d.description || '',
    status_value: d.status_value || '',
    assignee_role: d.assignee_role || '',
  };
}

function walkLinearTasks(startId, nodesById, edges, stopIds) {
  const steps = [];
  let cur = startId;
  const visited = new Set();
  while (cur && !stopIds.has(cur)) {
    if (visited.has(cur)) break;
    visited.add(cur);
    const node = nodesById[cur];
    if (!node) break;
    if (node.type === 'end') break;
    if (node.type === 'task') {
      if (node.data?.description?.startsWith('Ref: ') || node.data?.description === 'Inline sub-process') {
        break;
      }
      steps.push(taskFromNode(node));
      const outs = outgoing(edges, cur);
      if (outs.length !== 1) break;
      cur = outs[0].target;
      continue;
    }
    break;
  }
  return { steps, nextId: cur };
}

export function decompileTemplate(template) {
  const nodes = template?.nodes || [];
  const edges = template?.edges || [];
  if (!nodes.length) {
    return { blocks: [newTask()], advanced: false };
  }

  const nodesById = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const start = nodes.find((n) => n.type === 'start');
  if (!start) {
    return { blocks: [], advanced: true };
  }

  const blocks = [];
  let advanced = false;
  let cur = start.id;
  const visited = new Set();

  while (cur) {
    if (visited.has(cur)) {
      advanced = true;
      break;
    }
    visited.add(cur);
    const node = nodesById[cur];
    if (!node || node.type === 'end') break;

    if (node.type === 'task') {
      blocks.push(taskFromNode(node));
      const outs = outgoing(edges, cur);
      if (outs.length === 1) {
        cur = outs[0].target;
        continue;
      }
      advanced = true;
      break;
    }

    if (node.type === 'exclusive') {
      const branchEdges = outgoing(edges, cur);
      const branches = branchEdges.map((e) => {
        const stopIds = new Set([node.id]);
        const { steps, nextId } = walkLinearTasks(e.target, nodesById, edges, stopIds);
        return {
          id: uid('br'),
          label: e.data?.label || e.label || 'Branch',
          condition: e.data?.condition || '',
          is_default: Boolean(e.data?.is_default),
          steps: steps.length ? steps : [newTask({ label: e.data?.label || 'Branch' })],
          _exitId: nextId,
        };
      });

      const exitIds = branches.map((b) => b._exitId).filter(Boolean);
      const mergeTargets = [...new Set(exitIds.map((id) => {
        const outs = outgoing(edges, id);
        return outs.length === 1 ? outs[0].target : null;
      }).filter(Boolean))];

      blocks.push({
        id: node.id,
        type: 'split',
        label: node.data?.label || 'Split condition',
        branches: branches.map(({ _exitId, ...rest }) => rest),
      });

      if (mergeTargets.length === 1 && nodesById[mergeTargets[0]]?.type === 'merge') {
        const afterMerge = outgoing(edges, mergeTargets[0]);
        cur = afterMerge.length === 1 ? afterMerge[0].target : null;
        continue;
      }
      if (mergeTargets.every((t) => nodesById[t]?.type === 'end')) {
        cur = null;
        continue;
      }
      advanced = mergeTargets.length > 1;
      break;
    }

    if (node.type === 'parallel') {
      const laneEdges = outgoing(edges, cur);
      const lanes = laneEdges.map((e) => {
        const { steps } = walkLinearTasks(e.target, nodesById, edges, new Set([node.id]));
        return {
          id: uid('lane'),
          label: e.data?.label || e.label || 'Lane',
          steps: steps.length ? steps : [newTask()],
        };
      });
      blocks.push({
        id: node.id,
        type: 'parallel',
        label: node.data?.label || 'Parallel paths',
        lanes,
      });
      advanced = true;
      break;
    }

    if (['merge', 'sync', 'start'].includes(node.type)) {
      const outs = outgoing(edges, cur);
      cur = outs.length === 1 ? outs[0].target : null;
      continue;
    }

    advanced = true;
    break;
  }

  if (!blocks.length && !advanced) {
    return { blocks: [newTask()], advanced: false };
  }

  return { blocks, advanced };
}

export function templateToSimple(template) {
  const { blocks, advanced } = decompileTemplate(template);
  return {
    name: template?.name || '',
    description: template?.description || '',
    blocks,
    advanced,
  };
}
